import { useMemo, useState } from "react";
import { formatRupiah } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Search, Trash2, X } from "lucide-react";

const PAY_FILTERS = [
  { v: "all", label: "Semua Status Bayar" },
  { v: "unpaid", label: "Belum Lunas" },
  { v: "dp", label: "DP / Sebagian" },
  { v: "paid", label: "Lunas" },
];
const PAY_BADGE = {
  unpaid: { label: "Belum Lunas", cls: "bg-yellow-100 text-yellow-800" },
  dp: { label: "DP", cls: "bg-blue-100 text-blue-700" },
  paid: { label: "Lunas", cls: "bg-emerald-100 text-emerald-700" },
};
const invDate = (x) => (x.date || x.created_at || "").slice(0, 10);

export default function InvoiceListView({ list, shipStatus, onOpen, onStatus, onDelete, fmtDate }) {
  const [q, setQ] = useState("");
  const [pay, setPay] = useState("all");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");

  const rows = useMemo(() => {
    const s = q.trim().toLowerCase();
    return list.filter((x) => {
      if (s && !`${x.buyer_name || ""} ${x.invoice_number || ""}`.toLowerCase().includes(s)) return false;
      if (pay !== "all" && (x.payment_status || "unpaid") !== pay) return false;
      const d = invDate(x);
      if (from && d < from) return false;
      if (to && d > to) return false;
      return true;
    });
  }, [list, q, pay, from, to]);
  const hasFilter = q || pay !== "all" || from || to;
  const reset = () => { setQ(""); setPay("all"); setFrom(""); setTo(""); };

  return (
    <div className="rounded-2xl border border-slate-200 bg-white" data-testid="invoice-list-panel">
      <div className="p-4 border-b border-slate-100 grid gap-3 md:grid-cols-[1fr_200px_auto] items-end">
        <div className="relative">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari nama pelanggan atau no. invoice" className="pl-9 rounded-xl" data-testid="invoice-search" />
        </div>
        <Select value={pay} onValueChange={setPay}>
          <SelectTrigger className="rounded-xl" data-testid="invoice-filter-status"><SelectValue /></SelectTrigger>
          <SelectContent>{PAY_FILTERS.map((p) => <SelectItem key={p.v} value={p.v} data-testid={`invoice-filter-${p.v}`}>{p.label}</SelectItem>)}</SelectContent>
        </Select>
        <div className="flex items-center gap-2 flex-wrap">
          <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="rounded-xl w-40" data-testid="invoice-filter-from" />
          <span className="text-xs text-slate-400">s/d</span>
          <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="rounded-xl w-40" data-testid="invoice-filter-to" />
          {hasFilter && <button onClick={reset} className="text-xs text-slate-500 hover:text-[#F0005A] flex items-center gap-1" data-testid="invoice-filter-reset"><X className="w-3.5 h-3.5" /> Reset</button>}
        </div>
      </div>
      <div className="overflow-x-auto" data-testid="invoice-list">
        {rows.length === 0 ? (
          <p className="text-sm text-slate-400 p-10 text-center" data-testid="invoice-list-empty">{list.length ? "Tidak ada invoice yang cocok dengan filter." : "Belum ada invoice."}</p>
        ) : (
          <table className="w-full text-sm min-w-[760px]">
            <thead><tr className="text-left text-xs text-slate-500 bg-slate-50">
              <th className="p-3 font-medium">No. Invoice</th><th className="p-3 font-medium">Pelanggan</th><th className="p-3 font-medium">Tanggal</th>
              <th className="p-3 font-medium text-right">Total</th><th className="p-3 font-medium">Pembayaran</th><th className="p-3 font-medium w-48">Status</th><th className="w-10"></th>
            </tr></thead>
            <tbody>
              {rows.map((x) => {
                const st = shipStatus.find((s) => s.v === (x.shipping_status || "menunggu")) || shipStatus[0];
                const pb = PAY_BADGE[x.payment_status] || PAY_BADGE.unpaid;
                return (
                  <tr key={x.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`invoice-row-${x.invoice_number}`}>
                    <td className="p-3"><button onClick={() => onOpen(x)} className="font-semibold text-[#00133F] hover:text-[#F0005A] font-mono whitespace-nowrap" data-testid={`invoice-open-${x.invoice_number}`}>{x.invoice_number}</button></td>
                    <td className="p-3 text-[#00133F]">{x.buyer_name}</td>
                    <td className="p-3 text-slate-500 whitespace-nowrap">{fmtDate(invDate(x))}</td>
                    <td className="p-3 text-right font-semibold whitespace-nowrap">{formatRupiah(x.grand_total)}</td>
                    <td className="p-3"><span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${pb.cls}`} data-testid={`invoice-pay-badge-${x.invoice_number}`}>{pb.label}</span></td>
                    <td className="p-3">
                      <Select value={x.shipping_status || "menunggu"} onValueChange={(v) => onStatus(x.id, v)}>
                        <SelectTrigger className={`h-8 rounded-full text-xs font-semibold border-0 ${st.cls}`} data-testid={`invoice-status-${x.invoice_number}`}><SelectValue /></SelectTrigger>
                        <SelectContent>{shipStatus.map((s) => <SelectItem key={s.v} value={s.v}>{s.label}</SelectItem>)}</SelectContent>
                      </Select>
                    </td>
                    <td className="p-3"><button onClick={() => onDelete(x.id)} className="text-slate-300 hover:text-red-500 p-1" data-testid={`invoice-delete-${x.invoice_number}`}><Trash2 className="w-4 h-4" /></button></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
      <p className="text-xs text-slate-400 px-4 py-3 border-t border-slate-100" data-testid="invoice-list-count">Menampilkan {rows.length} dari {list.length} invoice</p>
    </div>
  );
}
